import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './hp-nav-flyout.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import hfMark from '../icon/assets/icons/hf-mark.svg';

export default class NavFlyout extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      links: { type: Array },
      isOpen: { type: Boolean },
      linkClass: { type: String },
      primeLinks: { type: Array },
      search: { type: Object },
      subLinks: { type: Array },
    };
  }

  constructor() {
    super();
    this.activeItemClass = '';
    this.linkClass = '';
    this.isOpen = false;
    this.mainLinks = [];
    this.primeLinks = [];
    this.subLinks = [];
    this.subLinks2 = [];
    this.lastTier = [];
  }

  firstUpdated() {
    if (this.links.length) {
      this.mainLinks = [...this.links]
      this.lastTier = this.mainLinks.splice(-1)[0]
      this.primeLinks = this.mainLinks.filter(link => link.url.length);

      this.subLinks = [
        ...this.mainLinks.map(link => {
          return link.sub.length ? link.sub : [];
        }),
      ];

      this.subLinks2 = this.subLinks.map((subLink, index) => {
        return subLink.filter(link => link.sub.length);
      }).filter(link => link.length);
    }

    // Add visibility hidden for accessibility.
    if (!this.isOpen) {
      const navFlyout = this.shadowRoot.querySelector('.nav-flyout__wrapper');
      navFlyout.classList.add("nav-flyout--hidden");
    }
  }

  async updated() {
    this._hideNavFlyout();
  }

  _hideNavFlyout() {
    const navFlyout = this.shadowRoot.querySelector('.nav-flyout__wrapper');
    // Visibility hidden after the transition for accessibility.
    navFlyout.addEventListener('transitionend', () => {
      if (!this.isOpen) {
        navFlyout.classList.add("nav-flyout--hidden");
      }
    });
    if (this.isOpen) {
      navFlyout.classList.remove("nav-flyout--hidden");
    }
  }

  renderList(list) {
    if (list) {
      let templateResult = [];
      const links = list.filter(link => !link.sub.length);

      links.map(subLink => {
        templateResult.push(
          html`
            <li class="nav-flyout__sub-links-item">
            <hf-link
              href=${subLink.url}
              target="_self"
              styleclass="nav-flyout__sub-link"
              class="nav-flyout__sub-link"
            >
              ${subLink.iconName
                ? html`
                    <hf-icon
                      name="${subLink.iconName}"
                      class="nav-flyout__sub-link--icon"
                      height="24"
                      width="24"
                    ></hf-icon>
                  `
                : nothing}
              <span class="nav-flyout__sub-link-text">${subLink.text}</span>
            </hf-link>
          </li>`
        );
      });

      return templateResult;
    }
  }

  menuItemOnClick(event, query_selector, hasSubMenu) {
    if (hasSubMenu) {
      event.preventDefault();

      let parentNode;
      switch (event.target.tagName) {
        case 'HF-LINK':
          parentNode = event.target;
          break;
        case 'A':
          parentNode = event.target;
          break;
        case 'LI':
          parentNode = event.target.querySelector('.nav-flyout__link');
          break;
        case 'HF-ICON':
          parentNode = event.target.parentNode;
          break;
        case 'SPAN':
          parentNode = event.target.parentNode;
          break;
      }

      const currentLinkIsActive = parentNode.classList.contains('active');
      const nav_flyout__links = this.shadowRoot.querySelectorAll( '.nav-flyout__link');
      nav_flyout__links.forEach(
        element => element.classList.remove("active")
      );

      const ul_wrappers = this.shadowRoot.querySelectorAll( '.nav-flyout__sub-links-wrapper');
      ul_wrappers.forEach(
        element => element.classList.remove("active")
      );

      if (!currentLinkIsActive) {
        parentNode.classList.add('active');

        const selected_ul_wrappers = this.shadowRoot.querySelectorAll(query_selector);
        selected_ul_wrappers.forEach(
          element => element.classList.add("active")
        );

      }
    }
  }

  render() {
    return html`<div
      class="${classMap({
        'nav-flyout__wrapper': true,
        [`nav-flyout--open`]: this.isOpen,
        [`nav-flyout--closed`]: !this.isOpen,
      })}"
    >
      <div class="nav-flyout__header">
        <hf-link href="/" target="_self" ariaLabel="HealthFirst">
          <div class="nav-flyout__logo-wrapper">
            <hf-icon class="nav-flyout__logo" name="hf-logo"></hf-icon>
          </div>
        </hf-link>
        <hf-search
          class="nav-flyout__search"
          placeholder="${this.search.text}"
          searchUrl="${this.search.url}"
        ></hf-search>
      </div>
      <div class="nav-flyout__links-wrapper">
        <ul class="nav-flyout__prime-links-wrapper">
          ${this.mainLinks.map((link, index) => {
            return html`
              <li
                @click=${event => this.menuItemOnClick(event, `.sub-links-wrapper--${index}`, link.sub.length > 0)}
                class="nav-flyout__prime-links-item">
                <hf-link
                  href=${link.url}
                  target="_self"
                  styleclass="nav-flyout__link"
                  class="${classMap({
                    'nav-flyout__link': true,
                    'nav-flyout__link--submenu': link.sub.length > 0,
                  })}"
                >
                  <span class="nav-flyout__link-text">${link.text}</span>
                  ${link.sub ?
                    html`
                      <hf-icon
                        name="chevron-down"
                        class="nav-flyout__link--icon"
                        height="24"
                        width="24">
                      </hf-icon>`
                    : nothing}
                </hf-link>
              </li>
            `;
          })}
        </ul>
        ${this.mainLinks.map((link, index) => {
          return html`
            <ul
              class="${classMap({
                'nav-flyout__sub-links-wrapper': true,
                [`sub-links-wrapper--${index}`]: true,
              })}"
            >
              ${this.renderList(link.sub)}
            </ul>
          `;
        })}

        ${this.lastTier && this.lastTier.sub && Array.isArray(this.lastTier.sub) ? html`
          <ul class="nav-flyout__sub-links-wrapper--2">
            ${this.renderList(this.lastTier.sub)}
          </ul>
        ` : nothing}
      </div>
      <span class="nav-flyout__icon">${unsafeHTML(hfMark)}</span>
    </div>`;
  }
}

customElements.define('hf-hp-nav-flyout', NavFlyout);
